import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Select, MenuItem, FormControl, InputLabel,
    Card, CardContent, Chip, CircularProgress, TextField, Link,
} from '@mui/material';
import {
    AttachMoney as MoneyIcon, Token as TokenIcon,
    TrendingUp as TrendingIcon, Speed as SpeedIcon,
} from '@mui/icons-material';
import { getLLMUsageStats } from '../../services/adminApi';
import { getTenants } from '../../services/adminApi';
import { UsageStatsRow, Tenant } from '../../services/types';

const formatCost = (cost: number): string => {
    if (cost === 0) return '$0.00';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(2)}`;
};

const formatTokens = (tokens: number): string => {
    if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M`;
    if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(1)}K`;
    return tokens.toString();
};

const formatDateTime = (isoStr?: string): string => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

interface SummaryCardProps {
    title: string;
    value: string;
    icon: React.ReactNode;
    color: string;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ title, value, icon, color }) => (
    <Card sx={{ flex: 1, minWidth: 200 }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ color, display: 'flex', alignItems: 'center' }}>{icon}</Box>
            <Box>
                <Typography variant="caption" color="text.secondary">{title}</Typography>
                <Typography variant="h5" fontWeight={600}>{value}</Typography>
            </Box>
        </CardContent>
    </Card>
);

/** Pretty-print an agent name: source_summarizer → Source Summarizer */
const formatAgent = (name?: string): string => {
    if (!name) return '—';
    return name
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
};

const UsageMonitoring: React.FC = () => {
    const [groupBy, setGroupBy] = useState<string>('agent');
    const [tenantFilter, setTenantFilter] = useState<string>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    const { data: tenants = [] } = useQuery(['tenants'], getTenants);

    const queryParams = useMemo(() => ({
        group_by: groupBy,
        ...(tenantFilter && { tenant_id: tenantFilter }),
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
    }), [groupBy, tenantFilter, startDate, endDate]);

    const { data: usageStats = [], isLoading } = useQuery(
        ['llm-usage', queryParams],
        () => getLLMUsageStats(queryParams),
    );

    const totals = useMemo(() => {
        return usageStats.reduce(
            (acc: { totalCost: number; totalTokens: number; totalPromptTokens: number; totalCompletionTokens: number; totalCalls: number }, row: UsageStatsRow) => ({
                totalCost: acc.totalCost + (row.total_cost || 0),
                totalTokens: acc.totalTokens + (row.total_tokens || 0),
                totalPromptTokens: acc.totalPromptTokens + (row.total_prompt_tokens || 0),
                totalCompletionTokens: acc.totalCompletionTokens + (row.total_completion_tokens || 0),
                totalCalls: acc.totalCalls + (row.total_calls || 0),
            }),
            { totalCost: 0, totalTokens: 0, totalPromptTokens: 0, totalCompletionTokens: 0, totalCalls: 0 }
        );
    }, [usageStats]);

    const avgCostPerCall = totals.totalCalls > 0 ? totals.totalCost / totals.totalCalls : 0;

    /** Derive the primary label for a row based on the current group_by */
    const getGroupLabel = (row: UsageStatsRow): string => {
        switch (groupBy) {
            case 'none':
                return formatAgent(row.agent_name);
            case 'agent':
                return formatAgent(row.agent_name);
            case 'provider':
                return row.provider_name || '—';
            case 'model':
                return `${row.provider_name || ''}/${row.model_name || ''}`;
            case 'tenant':
                return row.tenant_name || row.tenant_id || '—';
            case 'day':
                return row.day || '—';
            default:
                return '—';
        }
    };

    const isIndividual = groupBy === 'none';
    const showModel = groupBy === 'agent' || isIndividual;
    const showBatch = groupBy === 'agent' || isIndividual;
    const showTenant = groupBy === 'agent' || groupBy === 'provider' || groupBy === 'model' || isIndividual;

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Usage Monitoring</Typography>

            {/* Summary Cards */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <SummaryCard
                    title="Total Cost"
                    value={formatCost(totals.totalCost)}
                    icon={<MoneyIcon fontSize="large" />}
                    color="#2e7d32"
                />
                <SummaryCard
                    title="Total Tokens"
                    value={formatTokens(totals.totalTokens)}
                    icon={<TokenIcon fontSize="large" />}
                    color="#1565c0"
                />
                <SummaryCard
                    title="Total API Calls"
                    value={totals.totalCalls.toLocaleString()}
                    icon={<TrendingIcon fontSize="large" />}
                    color="#e65100"
                />
                <SummaryCard
                    title="Avg Cost / Call"
                    value={formatCost(avgCostPerCall)}
                    icon={<SpeedIcon fontSize="large" />}
                    color="#6a1b9a"
                />
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Group By</InputLabel>
                    <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} label="Group By">
                        <MenuItem value="none">None (Individual Records)</MenuItem>
                        <MenuItem value="agent">Agent</MenuItem>
                        <MenuItem value="provider">Provider</MenuItem>
                        <MenuItem value="model">Model</MenuItem>
                        <MenuItem value="tenant">Tenant</MenuItem>
                        <MenuItem value="day">Day</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Tenant</InputLabel>
                    <Select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} label="Tenant">
                        <MenuItem value="">All Tenants</MenuItem>
                        {tenants.map((t: Tenant) => <MenuItem key={t.tenant_id} value={t.tenant_id}>{t.name}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField
                    label="Start Date" type="date" size="small"
                    value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label="End Date" type="date" size="small"
                    value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Paper>

            {/* Usage Table */}
            {isLoading ? <CircularProgress /> : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {isIndividual ? 'Agent' : groupBy === 'day' ? 'Date' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}
                                </TableCell>
                                {showModel && <TableCell>Model</TableCell>}
                                {showTenant && <TableCell>Tenant</TableCell>}
                                {showBatch && <TableCell>Batch</TableCell>}
                                <TableCell>Date/Time</TableCell>
                                <TableCell align="right">API Calls</TableCell>
                                <TableCell align="right">Prompt Tokens</TableCell>
                                <TableCell align="right">Completion Tokens</TableCell>
                                <TableCell align="right">Total Tokens</TableCell>
                                <TableCell align="right">Total Cost</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {usageStats.map((row: UsageStatsRow, index: number) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Chip label={getGroupLabel(row)} size="small" variant="outlined" />
                                    </TableCell>
                                    {showModel && (
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.model_name || '—'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    {showTenant && (
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {row.tenant_name || row.tenant_id || '—'}
                                            </Typography>
                                        </TableCell>
                                    )}
                                    {showBatch && (
                                        <TableCell>
                                            {row.batch_id ? (
                                                <Link
                                                    href={`/batches/${row.batch_id}/prompts`}
                                                    underline="hover"
                                                    sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                                                >
                                                    {row.batch_id.substring(0, 8)}...
                                                </Link>
                                            ) : '—'}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                            {formatDateTime(isIndividual ? row.created_at : row.latest_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">{row.total_calls?.toLocaleString() || 0}</TableCell>
                                    <TableCell align="right">{formatTokens(row.total_prompt_tokens || 0)}</TableCell>
                                    <TableCell align="right">{formatTokens(row.total_completion_tokens || 0)}</TableCell>
                                    <TableCell align="right">{formatTokens(row.total_tokens || 0)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatCost(row.total_cost || 0)}</TableCell>
                                </TableRow>
                            ))}
                            {usageStats.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={showModel && showBatch && showTenant ? 10 : 7} align="center">
                                        No usage data yet. Run a pipeline to start tracking costs.
                                    </TableCell>
                                </TableRow>
                            )}
                            {usageStats.length > 0 && (
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell><strong>Total</strong></TableCell>
                                    {showModel && <TableCell />}
                                    {showTenant && <TableCell />}
                                    {showBatch && <TableCell />}
                                    <TableCell />
                                    <TableCell align="right"><strong>{totals.totalCalls.toLocaleString()}</strong></TableCell>
                                    <TableCell align="right"><strong>{formatTokens(totals.totalPromptTokens)}</strong></TableCell>
                                    <TableCell align="right"><strong>{formatTokens(totals.totalCompletionTokens)}</strong></TableCell>
                                    <TableCell align="right"><strong>{formatTokens(totals.totalTokens)}</strong></TableCell>
                                    <TableCell align="right"><strong>{formatCost(totals.totalCost)}</strong></TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default UsageMonitoring;
